import { FC, useMemo, useState } from 'react'

import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import { GridColDef, GridPaginationModel, GridSortDirection, GridSortModel } from '@mui/x-data-grid'

import { ALL_NUMBER_OPERATORS, ALL_STRING_OPERATORS } from 'constants/table-filters'

import { useGetProfitableEventsQuery } from 'services/dashboard-analytics/api'
import { DashboardAnalyticsFilter } from 'services/dashboard-analytics/types'

import CustomDataGrid from 'components/table/DataGrid'

import { camelToSnake } from 'utils/text'

interface ProfitableEventsTableProps {
  filters: DashboardAnalyticsFilter[]
}

const ProfitableEventsTable: FC<ProfitableEventsTableProps> = ({ filters }) => {
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Match',
        type: 'String',
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterOperators: ALL_STRING_OPERATORS,
      },
      {
        field: 'ticketsBought',
        headerName: 'Tickets Bought',
        type: 'Number',
        flex: 1,
        resizable: true,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterOperators: ALL_NUMBER_OPERATORS,
      },
      {
        field: 'ticketsSold',
        headerName: 'Tickets Sold',
        type: 'Number',
        flex: 1,
        resizable: true,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterOperators: ALL_NUMBER_OPERATORS,
      },
      {
        field: 'ticketsLeft',
        headerName: 'Tickets Left',
        type: 'Number',
        flex: 1,
        resizable: true,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterOperators: ALL_NUMBER_OPERATORS,
      },
      {
        field: 'totalCost',
        headerName: 'Total cost',
        type: 'Number',
        flex: 1,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterOperators: ALL_NUMBER_OPERATORS,
      },
      {
        field: 'totalRevenue',
        headerName: 'Total Revenue',
        type: 'Number',
        flex: 1,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterOperators: ALL_NUMBER_OPERATORS,
      },
      {
        field: 'realizedProfit',
        headerName: 'Realized Profit',
        type: 'Number',
        flex: 1,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        filterOperators: ALL_NUMBER_OPERATORS,
      },
      {
        field: 'totalProfit',
        headerName: 'Total Profit',
        type: 'Number',
        flex: 1,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        filterOperators: ALL_NUMBER_OPERATORS,
      },
      {
        field: 'roi',
        headerName: 'ROI',
        type: 'Number',
        flex: 1,
        resizable: true,
        headerAlign: 'center',
        align: 'center',
        filterOperators: ALL_NUMBER_OPERATORS,
        valueFormatter: ({ value: v }) => (v || v === 0 ? v : '-'),
      },
      {
        field: 'margin',
        headerName: 'Margin',
        type: 'Number',
        flex: 1,
        resizable: true,
        headerAlign: 'center',
        align: 'center',
        filterOperators: ALL_NUMBER_OPERATORS,
        valueFormatter: ({ value: v }) => (v || v === 0 ? v : '-'),
      },
      {
        field: 'avgTicketProfit',
        headerName: 'AVG Ticket Profit',
        type: 'Number',
        flex: 1,
        resizable: true,
        headerAlign: 'center',
        align: 'center',
        filterOperators: ALL_NUMBER_OPERATORS,
        valueFormatter: ({ value: v }) => (v || v === 0 ? v : '-'),
      },
    ],
    []
  )
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      sort: 'desc',
      field: 'totalProfit',
    },
  ])

  const { data, isFetching } = useGetProfitableEventsQuery(
    {
      filters: filters,
      page: {
        size: paginationModel.pageSize,
        pageNumber: paginationModel.page,
      },
      orderBy: camelToSnake(sortModel?.[0]?.field),
      direction: sortModel?.[0]?.sort as GridSortDirection,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  )
  return (
    <Box sx={{ height: '500px', width: '100%', boxShadow: '0px 2px 10px 2px rgba(0, 0, 0, 0.1)' }}>
      <CustomDataGrid
        rows={data?.data || []}
        columns={columns}
        columnVisibilityModel={{
          matchNumber: false,
          description: false,
          isFullyCompletedHp: false,
        }}
        rowCount={data?.page?.totalElements || 0}
        loading={isFetching}
        onSearchChange={() => {}}
        searchValue={''}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        disableColumnMenu={true}
        toolbar={() => (
          <Typography
            sx={{
              p: '8px 16px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            }}
            variant="h3"
          >
            Profitable Events
          </Typography>
        )}
        rowHeight={80}
        sx={{
          '& .MuiDataGrid-virtualScrollerContent': {
            minHeight: '200px !important',
          },
        }}
      />
    </Box>
  )
}

export default ProfitableEventsTable
